<template>
  <div>
    <empty-state
      v-if="loading || !data || Object.keys(data).length === 0"
      :loading="loading"
      :title="loading ? $t('loading') : $t('no_data_available')"
      :subtitle="!loading ? $t('no_orders_found') : ''"
      icon="mdi-cart"
    />
    <div v-else>
      <template v-for="(dateData, date) in data">
        <div
          class="d-flex align-items-center mb-2"
          style="justify-content: space-between"
        >
          <h3>
            <v-icon small>mdi-calendar</v-icon>
            {{ formatDate(date) }}
          </h3>
        </div>

        <!-- Mobile View -->
        <template v-if="device.isMobile">
          <v-card
            v-for="(item, index) in customSearch(dateData, headers, searchQuery, [
              'name',
              'lastname',
              'phone',
              'email',
              'orders_guest',
            ])"
            :key="`mobile-${date}-${index}`"
            class="mb-3 ordera-box-shadow"
            outlined
          >
            <!-- Header: ID & Status -->
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle class="text-caption grey--text">
                  ID:
                  {{
                    item.orders_guest && item.orders_guest[0]
                      ? item.orders_guest[0].id
                      : ""
                  }}
                </v-list-item-subtitle>
                <v-list-item-title
                  class="font-weight-bold user-name-link"
                  @click="showUserDetails(item)"
                >
                  {{ toLatin(item.name) }} {{ toLatin(item.lastname) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.phone }}
                </v-list-item-subtitle>
                <v-list-item-subtitle
                  v-if="
                    isAdmin &&
                    item.orders_guest &&
                    item.orders_guest[0] &&
                    item.orders_guest[0].restaurant
                  "
                  class="orange--text restaurant-link"
                  @click="showRestaurantDetails(item.orders_guest[0].restaurant)"
                >
                  <v-icon x-small color="orange">mdi-store</v-icon>
                  {{ item.orders_guest[0].restaurant.name }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-menu v-if="item.orders_guest && item.orders_guest[0]" offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      small
                      :color="getStatusColor(item.orders_guest[0].state)"
                      :text-color="getStatusTextColor(item.orders_guest[0].state)"
                      v-bind="attrs"
                      v-on="on"
                      class="cursor-pointer"
                      :loading="changingStatusOrderId === item.orders_guest[0].id"
                    >
                      {{ $t(item.orders_guest[0].state) }}
                      <v-icon x-small right>mdi-chevron-down</v-icon>
                    </v-chip>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-for="status in orderStatuses"
                      :key="status.value"
                      @click="
                        $emit('change-status', {
                          order: item.orders_guest[0],
                          guest: item,
                          newStatus: status.value,
                        })
                      "
                      :disabled="item.orders_guest[0].state === status.value"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small :color="getStatusColor(status.value)">
                          {{ status.icon }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t(status.value) }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>

            <v-divider></v-divider>

            <!-- Address & Time -->
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle class="text-caption grey--text">
                  {{ $t("address") }}
                </v-list-item-subtitle>
                <v-list-item-title v-if="item.orders_guest && item.orders_guest[0]">
                  <div v-if="item.orders_guest[0].delivery_type === 0">
                    <v-icon small>mdi-motorbike</v-icon>
                    {{ $t("delivery_to_address") }}
                    <div v-if="item.orders_guest[0].location" class="text-caption mt-1">
                      {{ item.orders_guest[0].location.address }}
                    </div>
                  </div>
                  <div v-else-if="item.orders_guest[0].delivery_type === 1">
                    <v-icon small>mdi-silverware-fork-knife</v-icon>
                    {{ $t("pickup_in_restaurant") }}
                  </div>
                  <div v-else-if="item.orders_guest[0].delivery_type === 2">
                    <v-icon small>mdi-home</v-icon>
                    {{ $t("consumption_in_restaurant") }}
                  </div>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.orders_guest && item.orders_guest[0]">
                <div class="text-caption grey--text">{{ $t("time") }}</div>
                <div class="font-weight-medium">
                  {{
                    item.orders_guest[0].delivery_time
                      ? item.orders_guest[0].delivery_time.substring(0, 5)
                      : ""
                  }}
                </div>
              </v-list-item-action>
            </v-list-item>

            <v-divider></v-divider>

            <!-- Orders list -->
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle class="text-caption grey--text mb-2">
                  {{ $t("ordered") }}
                </v-list-item-subtitle>
                <div v-if="item.orders_guest && item.orders_guest.length">
                  <div v-for="(order, idx) in item.orders_guest" :key="idx">
                    <div
                      v-for="(transaction, tidx) in order.order_guest_transactions"
                      :key="tidx"
                      class="d-flex justify-space-between mb-1"
                    >
                      <span>
                        {{ transaction.quantity }}x
                        {{ transaction.food ? transaction.food.name : "" }}
                      </span>
                      <span class="font-weight-medium">
                        {{ (transaction.price * transaction.quantity).toFixed(1) }}
                        {{ restaurantCurrency }}
                      </span>
                    </div>
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>

            <v-divider></v-divider>

            <!-- Total & Details button -->
            <v-card-actions class="justify-space-between">
              <v-btn
                text
                color="orange"
                small
                @click="
                  $emit('show-details', { order: item.orders_guest[0], guest: item })
                "
              >
                <v-icon small left>mdi-clipboard-text</v-icon>
                {{ $t("order_details") }}
              </v-btn>
              <div class="text-h6 font-weight-bold primary--text">
                {{ calculateTotal(item).toFixed(1) }} {{ restaurantCurrency }}
              </div>
            </v-card-actions>
          </v-card>
        </template>

        <!-- Desktop View -->
        <v-data-table
          v-else
          :key="`table-${date}`"
          :headers="headers"
          :items="
            customSearch(dateData, headers, searchQuery, [
              'name',
              'lastname',
              'phone',
              'email',
              'orders_guest',
            ])
          "
          class="striped-table ordera-box-shadow mb-6"
          :loading="loading"
          mobile-breakpoint="0"
          hide-default-footer
          :items-per-page="-1"
        >
          <template
            v-for="header in headers"
            v-slot:[`header.${header.value}`]="{ header }"
          >
            <span v-if="header.value === 'orders'" :key="header.value">
              <div class="d-flex justify-space-between" style="min-width: 300px">
                <span>{{ header.text }}</span>
                <span style="font-size: 10px; font-weight: 700">
                  {{ $t("amount") }} ({{ restaurantCurrency }})
                </span>
              </div>
            </span>
            <span v-else>{{ header.text }}</span>
          </template>

          <template v-slot:item.num="{ item }">
            {{ item.orders_guest && item.orders_guest[0] ? item.orders_guest[0].id : "" }}
          </template>

          <template v-slot:item.nameAndType="{ item }">
            <span class="user-name-link primary--text" @click="showUserDetails(item)">
              {{ toLatin(item.name) }} {{ toLatin(item.lastname) }}
            </span>
            <br />
            {{ item.phone }}
          </template>

          <template v-slot:item.restaurant="{ item }">
            <div
              v-if="
                item.orders_guest &&
                item.orders_guest[0] &&
                item.orders_guest[0].restaurant
              "
              class="restaurant-link primary--text"
              @click="showRestaurantDetails(item.orders_guest[0].restaurant)"
            >
              <v-icon small color="orange">mdi-store</v-icon>
              {{ item.orders_guest[0].restaurant.name }}
            </div>
          </template>

          <template v-slot:item.address="{ item }">
            <div v-if="item.orders_guest && item.orders_guest[0]">
              <div v-if="item.orders_guest[0].delivery_type === 0">
                <v-icon small>mdi-motorbike</v-icon>
                {{ $t("delivery_to_address") }}
                <br />
                <span v-if="item.orders_guest[0].location">
                  {{ item.orders_guest[0].location.address }}
                </span>
              </div>
              <div v-else-if="item.orders_guest[0].delivery_type === 1">
                <v-icon small>mdi-silverware-fork-knife</v-icon>
                <span>{{ $t("pickup_in_restaurant") }}</span>
              </div>
              <div v-else-if="item.orders_guest[0].delivery_type === 2">
                <v-icon small>mdi-home</v-icon>
                {{ $t("consumption_in_restaurant") }}
              </div>
            </div>
          </template>

          <template v-slot:item.time="{ item }">
            <div v-if="item.orders_guest && item.orders_guest[0]">
              {{
                item.orders_guest[0].delivery_time
                  ? item.orders_guest[0].delivery_time.substring(0, 5)
                  : ""
              }}
            </div>
          </template>

          <template v-slot:item.orders="{ item }">
            <div v-if="item.orders_guest && item.orders_guest.length">
              <div v-for="(order, idx) in item.orders_guest" :key="idx">
                <div
                  v-for="(transaction, tidx) in order.order_guest_transactions"
                  :key="tidx"
                  style="display: flex; justify-content: space-between"
                >
                  <span style="width: 70%">
                    {{ transaction.quantity }}x
                    {{ transaction?.food?.name || transaction?.meals?.name || "" }}
                  </span>
                  <span style="width: 30%; text-align: right">
                    {{ (transaction.price * transaction.quantity).toFixed(1) }}
                  </span>
                </div>
                <div class="mt-1">
                  <a
                    href="#"
                    class="text-decoration-none orange--text"
                    @click.prevent="$emit('show-details', { order, guest: item })"
                  >
                    <v-icon small color="orange">mdi-clipboard-text</v-icon>
                    {{ $t("order_details") }}
                  </a>
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item.total="{ item }">
            <span style="font-weight: bold">
              {{ calculateTotal(item).toFixed(1) }}
            </span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-menu v-if="item.orders_guest && item.orders_guest[0]" offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  :color="getStatusColor(item.orders_guest[0].state)"
                  :text-color="getStatusTextColor(item.orders_guest[0].state)"
                  v-bind="attrs"
                  v-on="on"
                  class="cursor-pointer"
                  :loading="changingStatusOrderId === item.orders_guest[0].id"
                >
                  {{ $t(item.orders_guest[0].state) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-chip>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="status in orderStatuses"
                  :key="status.value"
                  @click="
                    $emit('change-status', {
                      order: item.orders_guest[0],
                      guest: item,
                      newStatus: status.value,
                    })
                  "
                  :disabled="item.orders_guest[0].state === status.value"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small :color="getStatusColor(status.value)">
                      {{ status.icon }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(status.value) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </template>
    </div>

    <!-- Restaurant Details Dialog -->
    <restaurant-details-dialog
      v-model="restaurantDetailsDialog"
      :restaurant="selectedRestaurant"
    />

    <!-- User Details Dialog -->
    <guest-user-details-dialog v-model="userDetailsDialog" :user="selectedUser" />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import RestaurantDetailsDialog from "@/components/dialogs/RestaurantDetailsDialog.vue";
import GuestUserDetailsDialog from "@/components/dialogs/GuestUserDetailsDialog.vue";

export default {
  name: "ReportTable",
  components: {
    EmptyState,
    RestaurantDetailsDialog,
    GuestUserDetailsDialog,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    changingStatusOrderId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      orderStatuses: [
        { value: "pending", icon: "mdi-clock-outline" },
        { value: "accepted", icon: "mdi-check" },
        { value: "in-progress", icon: "mdi-progress-clock" },
        { value: "ready", icon: "mdi-food" },
        { value: "on-hold", icon: "mdi-pause" },
        { value: "delivered", icon: "mdi-truck-check" },
        { value: "completed", icon: "mdi-check-all" },
        { value: "cancelled", icon: "mdi-cancel" },
      ],
      restaurantDetailsDialog: false,
      selectedRestaurant: null,
      userDetailsDialog: false,
      selectedUser: null,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.user.user?.type === "admin";
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "70px" },
        {
          text: this.$t("name_surname_phone"),
          value: "nameAndType",
          sortable: true,
        },
      ];

      if (this.isAdmin) {
        baseHeaders.splice(1, 0, {
          text: this.$t("restaurant"),
          value: "restaurant",
          sortable: false,
        });
      }

      baseHeaders.push(
        { text: this.$t("address"), value: "address", sortable: false },
        { text: this.$t("time"), value: "time", sortable: false, width: "80px" },
        { text: this.$t("ordered"), value: "orders", sortable: false },
        { text: this.$t("total"), value: "total", sortable: false, width: "80px" },
        { text: this.$t("status"), value: "status", sortable: false, width: "120px" }
      );

      return baseHeaders;
    },
  },
  methods: {
    getStatusStyle(status) {
      const statusStyles = {
        pending: { bg: "orange", text: "white" },
        accepted: { bg: "blue", text: "white" },
        "in-progress": { bg: "cyan", text: "black" },
        ready: { bg: "purple", text: "white" },
        "on-hold": { bg: "grey", text: "white" },
        delivered: { bg: "green", text: "white" },
        completed: { bg: "green", text: "white" },
        cancelled: { bg: "red", text: "white" },
        deleted: { bg: "red", text: "white" },
      };
      return statusStyles[status] || { bg: "grey", text: "white" };
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("sr-Latn", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    calculateTotal(item) {
      if (!item.orders_guest || !item.orders_guest.length) return 0;

      let total = 0;
      item.orders_guest.forEach((order) => {
        if (order.order_guest_transactions) {
          order.order_guest_transactions.forEach((transaction) => {
            total += transaction.price * transaction.quantity;
          });
        }
      });

      return total;
    },
    getStatusColor(status) {
      return this.getStatusStyle(status).bg;
    },
    getStatusTextColor(status) {
      return this.getStatusStyle(status).text;
    },
    showRestaurantDetails(restaurant) {
      this.selectedRestaurant = restaurant;
      this.restaurantDetailsDialog = true;
    },
    showUserDetails(user) {
      this.selectedUser = user;
      this.userDetailsDialog = true;
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.restaurant-link {
  cursor: pointer;
}

.restaurant-link:hover {
  text-decoration-style: solid;
}

.user-name-link {
  cursor: pointer;
  color: inherit;
}

.user-name-link:hover {
  color: #fb6d3b;
  text-decoration: underline;
}
</style>
